import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falComment } from 'shared/@generated/icons/falComment';
import { falFingerprint } from 'shared/@generated/icons/falFingerprint';
import { falMessageHeart } from 'shared/@generated/icons/falMessageHeart';
import { falShieldKeyhole } from 'shared/@generated/icons/falShieldKeyhole';
import { useRoute } from 'shared/library/routes/route';
import { withNativeGradientFallback } from 'shared/library/native-gradient';
import { Platform, View } from 'react-native';
import { Listing, Paginator, Props, Breed, Animal } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';
import { PageBuilder } from 'shared/library/page-builder';
import { H1, H2 } from 'shared/components/title';
import { Text } from 'shared/components/text';
import { Image } from 'shared/components/image';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { ListingCard } from 'shared/components/listing-card';
import { BreedCard } from 'shared/components/breed-card';
import { useLocale } from 'shared/library/i18n/use-locale';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { AdBanner } from 'shared/components/ad-banner';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { HeroListingsCluster } from 'shared/components/hero-listings-cluster';
import { Browse } from 'shared/components/illustrations/browse';
import { Reservation } from 'shared/components/illustrations/reservation';
import { Payment } from 'shared/components/illustrations/payment';
import { Pickup } from 'shared/components/illustrations/pickup';
import CommentHeart from 'shared/assets/doodles/comment-heart.png';
import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { translateArray } from 'shared/library/i18n/get-translation-list';

interface BuyingACatScreenProps extends Props {
  listings: Paginator<Listing>;
  breeds: Paginator<Breed>;
}

const BuyingACatScreen = (props: BuyingACatScreenProps) => (
  <AdAnimalProvider animal={'cat'}>
    <BuyingACatContent {...props} />
  </AdAnimalProvider>
);

const BuyingACatContent = ({ listings, breeds }: BuyingACatScreenProps) => {
  const { t } = useTranslation('screens');
  const { market } = useLocale();
  const route = useRoute();
  const isNative = Platform.OS !== 'web';

  const faqItems = translateArray<{ question: string; answer: string }>(
    t,
    'buying_a_cat.faq.items',
    {
      country: market.country_name,
    },
  );

  const supportItems = [
    {
      title: t('buying_a_cat.hero.support_items.verified_profiles.title'),
      text: t('buying_a_cat.hero.support_items.verified_profiles.text'),
      icon: falFingerprint,
    },
    {
      title: t('buying_a_cat.hero.support_items.petpay.title'),
      text: t('buying_a_cat.hero.support_items.petpay.text'),
      icon: falShieldKeyhole,
    },
    {
      title: t('buying_a_cat.hero.support_items.personal_help.title'),
      text: t('buying_a_cat.hero.support_items.personal_help.text'),
      icon: falMessageHeart,
    },
  ];

  const processItems = translateArray<{
    title: string;
    text: string;
  }>(t, 'buying_a_cat.process.items');

  const fallbackProcessItem = { title: '', text: '' };

  const journeyItems: Array<{ title: string; text: string; image: ReactNode }> = [
    { ...(processItems[0] ?? fallbackProcessItem), image: <Browse /> },
    { ...(processItems[1] ?? fallbackProcessItem), image: <Reservation /> },
    { ...(processItems[2] ?? fallbackProcessItem), image: <Payment /> },
    { ...(processItems[3] ?? fallbackProcessItem), image: <Pickup /> },
  ];

  const heroListings = listings.data.filter(
    (listing) => listing.media_assets?.[0]?.url || listing.avatar?.url,
  );

  const listingsCount = listings.total ?? listings.data.length;
  const heroListingSource = heroListings.length > 0 ? heroListings : listings.data;

  const previewHeroListings =
    heroListingSource.length > 0
      ? Array.from(
          { length: 7 },
          (_, index) => heroListingSource[index % heroListingSource.length]!,
        )
      : [];

  const stats = [
    {
      value: '>100K',
      label: t('buying_a_cat.stats.sold_pets'),
    },
    {
      value: '>2K',
      label: t('buying_a_cat.stats.breeders'),
    },
    {
      value: '2019',
      label: t('buying_a_cat.stats.founded'),
    },
  ];

  return (
    <View>
      <View
        className={withNativeGradientFallback(
          clsx(
            'from-brand-cream-light via-brand-cream-light to-brand-cream bg-gradient-to-b',
            !isNative && 'relative left-1/2 right-1/2 -mx-[50vw] w-screen',
          ),
        )}
      >
        <View className={'px-3 py-6 md:px-6 md:py-10'}>
          <View className={'mx-auto w-full max-w-screen-xl'}>
            <View
              className={
                'flex flex-col-reverse gap-5 md:min-h-[38rem] md:flex-row md:items-center md:justify-between md:gap-8 xl:gap-10'
              }
            >
              <View className={'max-w-3xl flex-1 md:min-w-0 md:py-6'}>
                <H1
                  title={t('buying_a_cat.hero.title', { country: market.country_name })}
                  hero={true}
                  className={'max-w-xl'}
                  marginBottom={false}
                />

                <Text
                  className={
                    'text-brand-brown-dark/80 mt-5 max-w-xl text-lg leading-relaxed md:text-xl'
                  }
                >
                  {t('buying_a_cat.hero.text', { country: market.country_name })}
                </Text>

                <View className={'mt-8 gap-3 md:flex-row'}>
                  <Button
                    text={t('buying_a_cat.hero.view_listings')}
                    icon={falAngleRight}
                    color={'brown'}
                    href={route('listings.cats')}
                    className={'min-w-[13rem] transition-colors duration-200 hover:opacity-90'}
                  />
                  <Button
                    text={t('buying_a_cat.hero.view_breeds')}
                    icon={falAngleRight}
                    color={'white'}
                    href={route('breeds.cats')}
                    className={'hover:border-brand-brown-dark/30 transition-colors duration-200'}
                  />
                </View>

                <View className={'mt-6 self-center md:mt-10 md:self-start'}>
                  <View className={'relative pl-10 md:pl-14'}>
                    <View className={'absolute left-0 top-1/2 -translate-y-1/2'}>
                      <Image
                        src={CommentHeart}
                        alt={'Doodle'}
                        containerClassName={'size-9 md:size-12'}
                      />
                    </View>

                    <ReviewsSummaryCard hero={true} />
                  </View>
                </View>
              </View>

              <View
                className={
                  isNative
                    ? 'w-full'
                    : 'mt-8 w-full md:mx-0 md:mt-10 md:w-[25.5rem] md:flex-none lg:w-[29rem] xl:w-[33rem]'
                }
              >
                <HeroListingsCluster listings={previewHeroListings} />
              </View>
            </View>

            <View className={isNative ? 'mt-10 gap-6' : 'mt-10 grid gap-6 sm:grid-cols-3 sm:gap-8'}>
              {supportItems.map((item) => (
                <View key={item.title} className={'flex-row items-start gap-3 sm:pr-3'}>
                  <View
                    className={
                      'bg-brand-cream-dark flex size-10 shrink-0 items-center justify-center rounded-md'
                    }
                  >
                    <Icon icon={item.icon} className={'text-brand-brown-dark size-4'} />
                  </View>

                  <View className={'flex-1 gap-2'}>
                    <Text className={'text-brand-brown-dark text-lg font-semibold leading-6'}>
                      {item.title}
                    </Text>

                    <Text className={'text-brand-brown-dark/72 max-w-sm text-base leading-7'}>
                      {item.text}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          {!isNative && <AdBanner className={'mb-12 md:mb-20'} placement={'top'} />}

          <View className={'mx-auto max-w-screen-lg'}>
            <H2
              title={t('buying_a_cat.listings.title')}
              subtitle={t('buying_a_cat.listings.text', {
                country: market.country_name,
                count: listingsCount,
              })}
              center={true}
              marginTop={false}
            />
          </View>

          <View
            className={
              isNative
                ? 'mt-6 gap-6'
                : 'flex-cols-1 md:flex-cols-2 lg:flex-cols-4 mt-6 flex-row flex-wrap gap-6'
            }
          >
            {listings.data.slice(0, 8).flatMap((listing, index) => {
              const items = [
                <View key={listing.id}>
                  <ListingCard listing={listing} />
                </View>,
              ];

              if (!isNative && index === 1) {
                items.push(
                  <View key={`listing-mobile-ad-${listing.id}`} className={'w-full md:hidden'}>
                    <AdBanner loadImmediately={true} placement={'content'} />
                  </View>,
                );
              }

              return items;
            })}
          </View>

          <View className={'mt-8 items-start md:mt-10 md:items-center'}>
            <Button
              text={t('buying_a_cat.listings.cta')}
              icon={falAngleRight}
              color={'brown'}
              href={route('listings.cats')}
            />
          </View>
        </View>
      </View>

      <View
        className={clsx(
          'bg-brand-cream-light p-3 md:p-6 md:py-20',
          !isNative && 'relative left-1/2 right-1/2 -mx-[50vw] w-screen',
        )}
      >
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            id={'how-it-works'}
            title={t('buying_a_cat.process.title')}
            subtitle={t('buying_a_cat.process.text')}
            center={true}
            marginTop={false}
          />

          <View className={'mx-auto mt-3 max-w-screen-md gap-6'}>
            {journeyItems.map((item, index) => (
              <View
                key={item.title}
                className={clsx(
                  'gap-3 md:flex-row md:items-center md:gap-6',
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse',
                )}
              >
                <View className={'w-full md:w-2/5'}>{item.image}</View>

                <View className={'w-full md:flex-1'}>
                  <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                    {index + 1}. {item.title}
                  </Text>
                  <Text className={'text-brand-brown-dark/75 md:text-lg'}>{item.text}</Text>
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            title={t('buying_a_cat.why.title')}
            subtitle={t('buying_a_cat.why.text', { country: market.country_name })}
            center={true}
            marginTop={false}
          />

          <View
            className={
              isNative
                ? 'mx-auto mt-8 max-w-5xl gap-8'
                : 'mx-auto mt-8 grid max-w-5xl gap-8 md:grid-cols-3 md:gap-10'
            }
          >
            {stats.map((stat) => (
              <View key={stat.label} className={'items-center'}>
                <Text
                  className={'text-brand-brown-dark text-center text-2xl font-black md:text-3xl'}
                >
                  {stat.value}
                </Text>
                <Text className={'text-brand-brown-dark/70 mt-2 max-w-[18rem] text-center'}>
                  {stat.label}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            title={t('buying_a_cat.breeds.title')}
            subtitle={t('buying_a_cat.breeds.text')}
            center={true}
            marginTop={false}
          />

          <View
            className={
              isNative
                ? 'mt-6 flex-row flex-wrap gap-3'
                : 'flex-cols-2 md:flex-cols-5 mt-6 flex-row gap-3 md:gap-6'
            }
          >
            {breeds.data.slice(0, 10).map((breed) => (
              <View key={breed.id} style={isNative ? { width: '48%' } : undefined}>
                <BreedCard breed={breed} showActiveCount={true} />
              </View>
            ))}
          </View>

          <View className={'mt-8 items-start md:mt-10 md:items-center'}>
            <Button
              text={t('buying_a_cat.breeds.cta')}
              icon={falAngleRight}
              color={'brown'}
              href={route('breeds.cats')}
            />
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          {!isNative && <AdBanner className={'mb-12 md:mb-20'} placement={'content'} />}

          <View>
            <H2
              title={t('buying_a_cat.faq.title')}
              subtitle={t('buying_a_cat.faq.text')}
              center={true}
              marginTop={false}
            />

            <View className={isNative ? 'mt-6 gap-6' : 'mt-6 grid gap-6 md:grid-cols-2'}>
              {faqItems.map((item) => (
                <View key={item.question} className={'pb-2'}>
                  <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                    {item.question}
                  </Text>
                  <Text className={'text-brand-brown-dark/75 mt-2'}>{item.answer}</Text>
                </View>
              ))}
            </View>

            <View
              className={
                'mt-8 flex-col items-start gap-3 md:flex-row md:items-center md:justify-center'
              }
            >
              <Text className={'text-brand-brown-dark/75'}>{t('buying_a_cat.faq.help_text')}</Text>

              <Button
                text={t('buying_a_cat.faq.cta')}
                icon={falComment}
                iconPosition={'left'}
                color={'white'}
                href={route('help.index')}
              />
            </View>
          </View>

          <View className={'mt-12'}>
            <ReviewsSegment role={'buyer'} />
          </View>
        </View>
      </View>

      <View className={'bg-brand-cream p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            title={t('buying_a_cat.closing.title')}
            subtitle={t('buying_a_cat.closing.text')}
            center={true}
            marginTop={false}
          />

          <View className={'mt-3 flex-col items-center gap-3 md:flex-row md:justify-center'}>
            <Button
              text={t('buying_a_cat.closing.primary_cta')}
              icon={falAngleRight}
              color={'brown'}
              href={route('listings.cats')}
            />
            <Button
              text={t('buying_a_cat.closing.secondary_cta')}
              icon={falAngleRight}
              color={'white'}
              href={route('breeds.cats')}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

BuyingACatScreen.pageBuilder = new PageBuilder<BuyingACatScreenProps>()
  .request(({ req }) =>
    req<{ listings: Paginator<Listing> }>(apiRoute('listings.index'), {
      format: 'card',
      animal: Animal.Cat,
      per_page: 8,
    }),
  )
  .request(({ req }) =>
    req<{ breeds: Paginator<Breed> }>(apiRoute('breeds.recommended'), {
      animal: Animal.Cat,
      per_page: 10,
    }),
  );

export { BuyingACatScreen };
export type { BuyingACatScreenProps };
